import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type {
  ConformityV2BlockedReason,
  ConformityV2Framework,
} from "../../../api/conformityApi"
import type { McPillTone } from "../../shared/McPill"

type Translate = (key: TranslationKeys) => string

// Labels are the Supplier Studio verdict vocabulary (MC_DECL_VERDICT_*), so a
// framework reads the same in both studios.
const CLASS_KEYS: Record<string, TranslationKeys> = {
  favourable: "MC_DECL_VERDICT_COMPLIANT",
  conditional: "MC_DECL_VERDICT_COMPLIANT_WITH_CONDITIONS",
  indeterminate: "MC_DECL_VERDICT_PENDING",
  adverse: "MC_DECL_VERDICT_NON_COMPLIANT",
}

// McPill tones, the same family Supplier Studio v2 uses for its verdict
// chips (success / yellow / danger / grey), so both studios read alike.
const CLASS_TONES: Record<string, McPillTone> = {
  favourable: "success",
  conditional: "attention",
  indeterminate: "attention",
  adverse: "danger",
}

const BLOCKED_KEYS: Record<ConformityV2BlockedReason, TranslationKeys> = {
  gap: "MC_CONF_V2_BLOCKED_GAP",
  adverse: "MC_CONF_V2_BLOCKED_ADVERSE",
  dependency: "MC_CONF_V2_BLOCKED_DEPENDENCY",
  dependency_missing: "MC_CONF_V2_BLOCKED_DEPENDENCY_MISSING",
  unconfigured: "MC_CONF_V2_BLOCKED_UNCONFIGURED",
}

/** Status pill for a v2 framework row — label + McPill tone keyed by the
 *  rollup's declaration class. Unknown classes fall back to the grey look. */
export function frameworkClassPill(
  framework: Pick<ConformityV2Framework, "computedClass">,
  translate: Translate,
): { label: string; tone: McPillTone } {
  const key = CLASS_KEYS[framework.computedClass]
  return {
    label: key ? translate(key) : framework.computedClass,
    tone: CLASS_TONES[framework.computedClass] ?? CLASS_TONES.indeterminate,
  }
}

export function frameworkBlockedLabel(
  reason: ConformityV2BlockedReason,
  translate: Translate,
): string {
  return translate(BLOCKED_KEYS[reason] ?? "MC_CONF_V2_BLOCKED_GAP")
}

// ─── Design-canvas row status (the editor's five pill states) ────────────────

export type ConformityV2RowStatus =
  | "compliant"
  | "info"
  | "blocked"
  | "adverse"
  | "unavailable"
  | "limited"

/** Maps a persisted framework row onto the editor's pill states:
 *  unconfigured → unavailable, adverse finding → adverse, other blocks →
 *  blocked, override/scoped → limited, compliant_with_condition → info,
 *  else compliant. */
export function frameworkRowStatus(
  f: Pick<
    ConformityV2Framework,
    "blockedReason" | "statementVariant" | "overrideReason"
  >,
): ConformityV2RowStatus {
  if (f.blockedReason === "unconfigured") return "unavailable"
  if (f.blockedReason === "adverse") return "adverse"
  if (f.blockedReason) return "blocked"
  if (f.statementVariant === "scoped" || f.overrideReason) return "limited"
  if (f.statementVariant === "compliant_with_condition") return "info"
  return "compliant"
}

/** Blocks that keep a framework out of the document (the include checkbox
 *  is disabled, the gap list or adverse finding is shown instead). */
export function isBlockedStatus(status: ConformityV2RowStatus): boolean {
  return status === "blocked" || status === "adverse"
}

// Same vocabulary and tones as the Supplier Studio verdict chips.
const ROW_STATUS_KEYS: Record<ConformityV2RowStatus, TranslationKeys> = {
  compliant: "MC_DECL_VERDICT_COMPLIANT",
  info: "MC_DECL_VERDICT_COMPLIANT_WITH_CONDITIONS",
  blocked: "MC_DECL_VERDICT_PENDING",
  adverse: "MC_DECL_VERDICT_NON_COMPLIANT",
  unavailable: "MC_DECL_STUDIO_NO_VERDICT",
  limited: "MC_DECL_VERDICT_COMPLIANT_WITH_CONDITIONS",
}

const ROW_STATUS_TONES: Record<ConformityV2RowStatus, McPillTone> = {
  compliant: "success",
  info: "attention",
  blocked: "attention",
  adverse: "danger",
  unavailable: "info",
  limited: "attention",
}

export function frameworkRowPill(
  status: ConformityV2RowStatus,
  translate: Translate,
): { label: string; tone: McPillTone } {
  return {
    label: translate(ROW_STATUS_KEYS[status]),
    tone: ROW_STATUS_TONES[status],
  }
}

const GAP_REASON_KEYS: Record<string, TranslationKeys> = {
  missing: "MC_CONF_V2_EDIT_GAP_DATA_PENDING",
  no_supplier_data: "MC_CONF_V2_EDIT_GAP_DATA_PENDING",
  stale: "MC_CONF_V2_EDIT_GAP_STALE",
  no_bom: "MC_CONF_V2_EDIT_GAP_NO_BOM",
}

export function gapReasonLabel(reason: string, translate: Translate): string {
  const key = GAP_REASON_KEYS[reason]
  return key ? translate(key) : reason
}

/** Anchor id for a missing-own-SCIP detail row — used to jump the view to
 *  it (footer chip, refused-approve panel) and to focus it on landing. */
export function scipRowAnchorId(productId: string): string {
  return `conf-scip-${productId}`
}

/** "ECHA candidate list, version of 4 Feb 2026, 253 entries" — the register
 *  line under a framework name, from its persisted register references. */
export function registerLine(
  f: Pick<ConformityV2Framework, "registers" | "blockedReason">,
  translate: Translate,
  formatDate: (iso: string) => string,
): string {
  if (f.blockedReason === "unconfigured") {
    return translate("MC_CONF_V2_BLOCKED_UNCONFIGURED")
  }
  return f.registers
    .map((r) => {
      const parts = [r.name]
      if (r.publishedAt) parts.push(formatDate(r.publishedAt))
      if (r.substanceCount != null) parts.push(String(r.substanceCount))
      return parts.join(" · ")
    })
    .join(" · ")
}
